import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import { useAuth } from '@clerk/clerk-react';

import {
  createApiClient,
  forgetApiKey,
  revealApiKey,
  setApiKeyActive,
} from '../api.js';
import { useConfirm } from '../components/ConfirmDialog.jsx';
import { COPYABLE_CODE_CSS } from '../components/CopyableCode.jsx';
import { useToast } from '../components/Toast.jsx';
import { MintKeyForm } from '../components/MintKeyModal.jsx';
import { ScopePicker } from '../components/ScopePicker.jsx';
import { Avatar } from '../components/Avatar.jsx';
import {
  EditIcon,
  EyeIcon,
  EyeOffIcon,
  CopyIcon,
  DeleteIcon,
  DisableIcon,
  EnableIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  XIcon,
  SaveIcon,
} from '../components/icons';

/**
 * Tenant + project-scoped API keys admin view (card 053).
 *
 * One component drives two routes:
 *
 *   - `/projects/<slug>/api-keys` — project-scoped admin.  All four
 *     mint presets visible (Personal / Bookmarklet / Embed / MCP).
 *     This is the established surface; nothing changes for it.
 *
 *   - `/keys` — tenant-wide admin.  No project in scope, so the
 *     list endpoint returns every key on the tenant subject to the
 *     role-hierarchy gate (card 042).  Mint flow filters to
 *     system-only presets (`presetFilter="system"`) — personal /
 *     bookmarklet minting belongs on /my-keys, not here.
 *
 * Card 044's row polish + reveal-as-toast + edit modal are preserved
 * verbatim.  Card 048's purpose badge surfaces inline on each row.
 */
export function ApiKeys() {
  const { getToken } = useAuth();
  const { project } = useOutletContext() || {};
  const projectId = project?.project_id;
  // Two modes: project-scoped (driven by the parent ProjectDetail
  // outlet) and tenant-wide (the /keys route).  The shape of the
  // surface is identical; only the list filter + the mint preset
  // pool change.
  const tenantWide = !projectId;

  const api = useMemo(
    () => createApiClient(getToken, { projectId }),
    [getToken, projectId],
  );

  const [keys, setKeys] = useState([]);
  const [archived, setArchived] = useState([]);
  const [showArchived, setShowArchived] = useState(false);
  const [editing, setEditing] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentUserId, setCurrentUserId] = useState('');
  const [confirmNode, ask] = useConfirm();

  // Tenant identity → "yours / system / another teammate's" badges
  // on every row.  One round-trip on mount; the API call is cheap
  // and dedup'd by the API client cache.
  useEffect(() => {
    let alive = true;
    api('/v1/admin/team')
      .then((resp) => {
        if (!alive) return;
        const self = (resp?.members || []).find((m) => m.is_self);
        if (self) setCurrentUserId(self.platform_user_id);
      })
      .catch(() => { /* badges fall back to system / theirs */ });
    return () => { alive = false; };
  }, [api]);

  const load = useCallback(async () => {
    // Tenant-wide list works without a projectId; the project-scoped
    // surface, OTOH, is meaningless without one — wait for outlet
    // context to land before firing.
    if (!tenantWide && !projectId) return;
    setLoading(true);
    setError(null);
    try {
      const qs = projectId
        ? `?project_id=${encodeURIComponent(projectId)}`
        : '';
      const qsAll = projectId
        ? `${qs}&include_revoked=true`
        : '?include_revoked=true';
      const [active, all] = await Promise.all([
        api(`/v1/admin/api-keys${qs}`),
        api(`/v1/admin/api-keys${qsAll}`),
      ]);
      setKeys(active?.keys || []);
      setArchived((all?.keys || []).filter((k) => !k.active));
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [api, projectId, tenantWide]);

  useEffect(() => { load(); }, [load]);

  return (
    <section className="stack" style={{ marginTop: 4 }}>
      <style>{COPYABLE_CODE_CSS}</style>
      <style>{API_KEYS_ROW_CSS}</style>
      <div>
        <h2 style={{ margin: '0 0 4px' }}>
          {tenantWide ? 'API keys' : 'API keys'}
        </h2>
        <p style={{ color: 'var(--text-3)', margin: 0, fontSize: 13.5 }}>
          {tenantWide ? (
            <>
              Every API key on this tenant subject to your role.  Use
              this surface to mint system-owned keys (project embeds
              + shared backend integrations) and to revoke teammates'
              keys when they leave.  Personal keys for yourself live
              on <strong>My API keys</strong>.
            </>
          ) : (
            <>
              Each key carries this project's scope automatically — a
              bookmarklet or SDK embed using one of these keys lands
              in this project without the page author needing to set
              any header. Plaintext is shown once at creation;
              pk_live_* keys can be revealed again afterwards via the
              eye icon, sk_live_* values cannot (mint a fresh one if
              you've lost it).
            </>
          )}
        </p>
        <p style={{ color: 'var(--text-3)', margin: '6px 0 0', fontSize: 12.5 }}>
          Admins can see and revoke other teammates' keys but never
          read their value. Owners' personal keys stay private even
          from other Admins. Plaintext reveal works for your own
          pk_live keys only.
        </p>
      </div>

      <MintKeyForm
        api={api}
        projectId={projectId}
        /* On /keys (tenant-wide), filter the preset list to system
         * shapes — personal / bookmarklet minting belongs on the
         * /my-keys route where the client also pins owner_user_id. */
        presetFilter={tenantWide ? 'system' : undefined}
        forceOwnership={tenantWide ? 'system' : undefined}
        onCreated={load}
      />

      {loading && (
        <div className="state"><div className="state__title">Loading keys…</div></div>
      )}
      {error && !loading && (
        <div className="state state--error">
          <div className="state__title">Couldn't load keys</div>
          <div>{error}</div>
        </div>
      )}
      {!loading && !error && keys.length === 0 && (
        <div className="state">
          <div className="state__title">
            {tenantWide ? 'No keys on this tenant yet' : 'No keys for this project yet'}
          </div>
          <div>
            {tenantWide
              ? 'Mint a system-owned embed above, or create a project — every project ships with one auto-minted at create time.'
              : "Mint one above. You can still use a tenant-wide key from the tenant's seed data; project keys are optional but recommended for isolation."}
          </div>
        </div>
      )}
      {!loading && !error && keys.length > 0 && (
        <ul className="list">
          {keys.map((k) => (
            <KeyRow
              key={k.prefix}
              k={k}
              api={api}
              onChanged={load}
              onEdit={() => setEditing(k)}
              ask={ask}
              currentUserId={currentUserId}
            />
          ))}
        </ul>
      )}

      {!loading && !error && archived.length > 0 && (
        <section style={{ marginTop: 20 }}>
          <button
            type="button"
            className="btn btn--ghost"
            onClick={() => setShowArchived((s) => !s)}
            style={{ marginBottom: 8 }}
          >
            {showArchived ? <ChevronUpIcon /> : <ChevronDownIcon />}
            {showArchived ? 'Hide' : 'Show'} archived keys ({archived.length})
          </button>
          {showArchived && (
            <ul className="list">
              {archived.map((k) => (
                <KeyRow
                  key={k.prefix}
                  k={k}
                  api={api}
                  archived
                  onChanged={load}
                  onEdit={() => setEditing(k)}
                  ask={ask}
                  currentUserId={currentUserId}
                />
              ))}
            </ul>
          )}
        </section>
      )}

      {editing && (
        <EditKeyModal
          k={editing}
          api={api}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}

      {confirmNode}
    </section>
  );
}

function KeyRow({ k, api, archived = false, onChanged, onEdit, ask, currentUserId }) {
  const expired = k.expires_at && new Date(k.expires_at).getTime() < Date.now();
  // Card 042 — Admins / Owners can see other people's keys as
  // metadata-only.  When the server flags the row that way we hide
  // every mutating affordance + the reveal eye-icon; the user can
  // still see "this key exists" but can't act on it.  Falls back
  // to the row's own-ness check (currentUserId === owner_user_id)
  // for older API versions that don't surface the flag yet.
  const ownedByMe = (
    k.owner_user_id
      ? (currentUserId && k.owner_user_id === currentUserId)
      // System-owned: visible-as-full to Admin/Owner, the server
      // already gated.  is_metadata_only being false is the
      // canonical signal that the caller can act.
      : true
  );
  const metadataOnly = k.is_metadata_only ?? !ownedByMe;
  // Card 044 — owner identity strip next to the prefix.  Only
  // surface it for rows the caller doesn't own (otherwise the
  // "yours" badge above already covers it).  System-owned shows
  // "System"; others show "Display Name · email" muted.
  const showOwnerLabel = !ownedByMe;
  return (
    <li className="list__row api-key-row">
      <div className="list__primary api-key-row__primary">
        <span className="list__name api-key-row__name">
          {k.name || <em style={{ color: 'var(--text-3)' }}>untitled</em>}
          <KindBadge kind={k.kind || 'sk'} />
          <OwnershipBadge k={k} currentUserId={currentUserId} />
          <PurposeBadge k={k} />
          {!k.active && k.revoked_at && (
            <span className="badge badge--default" style={{ marginLeft: 6 }}>revoked</span>
          )}
          {!k.active && !k.revoked_at && (
            <span
              className="badge badge--default"
              style={{ marginLeft: 6 }}
              title="Disabled — flips back on with the Enable button. Validator 401s on use just like a revoked key."
            >
              disabled
            </span>
          )}
          {expired && k.active && <span className="badge badge--default" style={{ marginLeft: 6 }}>expired</span>}
          {metadataOnly && (
            <span
              className="badge badge--default"
              style={{ marginLeft: 6 }}
              title="You can see this key exists but can't read its value or act on it (role hierarchy)."
            >
              read-only
            </span>
          )}
        </span>
        <span className="list__meta api-key-row__meta">
          <code>{k.prefix}…</code>
          {showOwnerLabel && <OwnerInlineLabel k={k} />}
          {' · '}
          {k.project_id ? `project ${k.project_id}` : 'tenant-wide'}
          {' · '}
          created {formatDate(k.created_at)}
          {k.last_used_at && <> · last used {formatDate(k.last_used_at)}</>}
          {k.expires_at && <> · expires {formatDate(k.expires_at)}</>}
          {k.revoked_at && <> · revoked {formatDate(k.revoked_at)}</>}
        </span>
        {k.scopes && k.scopes.length > 0 && (
          <span className="list__meta" style={{ marginTop: 4 }}>
            scopes:{' '}
            {k.scopes.map((s) => (
              <code key={s} style={{ marginRight: 4, fontSize: 11 }}>{s}</code>
            ))}
          </span>
        )}
        {(!k.scopes || k.scopes.length === 0) && (
          <span className="list__meta" style={{ marginTop: 4, color: 'var(--text-3)', fontSize: 11.5 }}>
            full access (no scopes)
          </span>
        )}
      </div>
      <div className="list__actions api-key-row__actions">
        {/* Card 044 — icon-only buttons, 28×28, render order
         *  [eye] [pencil] [pause|play] [trash].  Disabled state
         *  fades + cursor: not-allowed via .icon-btn[aria-disabled]. */}
        {!archived && !metadataOnly && k.is_revealable && (
          <RevealIconButton k={k} api={api} />
        )}
        {!archived && !metadataOnly && (
          <IconBtn title="Edit name + scopes" onClick={onEdit} ariaLabel={`Edit key ${k.prefix}`}>
            <PencilIcon />
          </IconBtn>
        )}
        {/* Disable / Enable — separate from Revoke.  Disable keeps
         *  the row in the active list so the operator can flip it
         *  back when they're done investigating; Revoke moves it
         *  to Archived. */}
        {!archived && !metadataOnly && k.active && (
          <IconBtn
            title="Disable — keeps the row, can be re-enabled"
            ariaLabel={`Disable key ${k.prefix}`}
            onClick={() => ask({
              title: `Disable key ${k.prefix}…?`,
              body: (
                <>
                  <p>
                    The key stays in the active list but the API will 401
                    every request using it. Click <strong>Enable</strong>
                    later to flip it back on.
                  </p>
                  <p>
                    Use this when you suspect a leak and want to break
                    the key temporarily while you investigate. To remove
                    it permanently, use <strong>Revoke</strong>.
                  </p>
                </>
              ),
              confirmLabel: 'Disable',
              runOnConfirm: async () => {
                await setApiKeyActive(api, k.prefix, false);
                onChanged();
              },
            })}
          >
            <PauseIcon />
          </IconBtn>
        )}
        {!archived && !metadataOnly && !k.active && !k.revoked_at && (
          <IconBtn
            title="Enable — the API will start accepting this key again"
            ariaLabel={`Enable key ${k.prefix}`}
            onClick={() => ask({
              title: `Enable key ${k.prefix}…?`,
              body: 'The API will start accepting requests carrying this key again.',
              confirmLabel: 'Enable',
              runOnConfirm: async () => {
                await setApiKeyActive(api, k.prefix, true);
                onChanged();
              },
            })}
          >
            <PlayIcon />
          </IconBtn>
        )}
        {archived ? (
          <IconBtn
            title="Restore — start authorising requests again"
            ariaLabel={`Restore key ${k.prefix}`}
            onClick={() => ask({
              title: `Restore key ${k.prefix}…?`,
              body: `It'll start authorising requests again.`,
              confirmLabel: 'Restore',
              runOnConfirm: async () => {
                await setApiKeyActive(api, k.prefix, true);
                onChanged();
              },
            })}
          >
            <PlayIcon />
          </IconBtn>
        ) : (!metadataOnly && (
          <IconBtn
            title="Revoke — moves the key to Archived; 401s every request"
            ariaLabel={`Revoke key ${k.prefix}`}
            danger
            onClick={() => ask({
              title: `Revoke key ${k.prefix}…?`,
              body: (
                <>
                  <p>
                    Any page using this key will get 401 on its next request —
                    including your bookmarklet if you minted it from this key.
                  </p>
                  <p>
                    The key stays in the Archived list so you can still see its history.
                  </p>
                </>
              ),
              confirmLabel: 'Revoke',
              danger: true,
              // runOnConfirm keeps the dialog open + busy until the
              // backend acknowledges.  Any thrown error stays
              // visible inline; the dialog closes only on a clean
              // success.  Previously the close-then-run pattern
              // hid all failures behind a silent unhandled-promise
              // rejection, which is what was reported as
              // "revoke does nothing".
              runOnConfirm: async () => {
                await api(
                  `/v1/admin/api-keys/${encodeURIComponent(k.prefix)}`,
                  { method: 'DELETE' },
                );
                forgetApiKey(k.prefix);
                onChanged();
              },
            })}
          >
            <TrashIcon />
          </IconBtn>
        ))}
      </div>
    </li>
  );
}

/**
 * "Display Name · email" muted strip next to the prefix when the
 * caller isn't the owner.  System-owned (no owner_user_id) shows
 * "System".  Personal but un-hydrated rows (Clerk profile lookup
 * failed; rare) fall back to the raw user id.
 *
 * Renders a 16px avatar before the label so the owner is
 * recognisable at a glance — matches the HelpInbox / UserMenu /
 * Members surfaces that have used avatars all along.  System rows
 * use the brand-accent variant so they stand out from real
 * operators.
 */
function OwnerInlineLabel({ k }) {
  const owner = (k.owner_user_id || '').trim();
  if (!owner) {
    return (
      <>
        {' · '}
        <span className="api-key-row__owner">
          <Avatar name="System" identity="system" size={16} accent />
          System
        </span>
      </>
    );
  }
  const name = (k.owner_display_name || '').trim();
  const email = (k.owner_email || '').trim();
  const url = (k.owner_avatar_url || '').trim() || null;
  if (!name && !email) {
    return (
      <>
        {' · '}
        <span className="api-key-row__owner" title={owner}>
          <Avatar identity={owner} size={16} />
          {owner}
        </span>
      </>
    );
  }
  return (
    <>
      {' · '}
      <span className="api-key-row__owner">
        <Avatar url={url} name={name} email={email} identity={owner} size={16} />
        {name || email}
        {name && email && <> · {email}</>}
      </span>
    </>
  );
}

/**
 * Card 044 — generic icon-button.  28×28, transparent bg, subtle
 * hover tint, tooltip via title.  ``danger`` flips the hover into
 * the danger token range so destructive actions feel different from
 * benign ones.
 */
function IconBtn({ children, onClick, title, ariaLabel, danger = false, disabled = false }) {
  return (
    <button
      type="button"
      onClick={disabled ? undefined : onClick}
      title={title}
      aria-label={ariaLabel || title}
      aria-disabled={disabled || undefined}
      className={`icon-btn${danger ? ' icon-btn--danger' : ''}${disabled ? ' icon-btn--disabled' : ''}`}
    >
      {children}
    </button>
  );
}

// Icon-row aliases — historic names kept for the existing JSX call
// sites; bodies imported from the shared `components/icons` module.
const PencilIcon = EditIcon;
const PauseIcon = DisableIcon;
const PlayIcon = EnableIcon;
const TrashIcon = DeleteIcon;
const CopyToastIcon = CopyIcon;

/**
 * Card 044 — single-click reveal.  Click eye → POST /reveal →
 * toast (top-right) carrying the plaintext in a read-only `<input>`,
 * a Copy icon, a Hide icon.  Auto-hides after 30s; the eye icon
 * flips to eye-off while the toast is live so the operator can see
 * which row's plaintext is on screen.
 *
 * The pk plaintext is publishable by design (origin lockdown is the
 * security boundary, not secrecy) so we don't gate this behind a
 * confirm step — the toast copy carries the "anyone watching your
 * screen…" warning that the old ConfirmDialog used to.
 */
function RevealIconButton({ k, api }) {
  const { showToast, dismissToast } = useToast();
  const [revealedToastId, setRevealedToastId] = useState(null);
  // Track the active toast id via a ref so the unmount cleanup can
  // dismiss it without re-firing when state changes.
  const activeIdRef = useRef(null);

  useEffect(() => {
    activeIdRef.current = revealedToastId;
  }, [revealedToastId]);

  useEffect(() => () => {
    // If this row unmounts (e.g. the list reloads) while its toast
    // is still up, dismiss the toast so a stale plaintext doesn't
    // float forever.
    if (activeIdRef.current) dismissToast(activeIdRef.current);
  }, [dismissToast]);

  async function doReveal() {
    let plaintext = '';
    try {
      plaintext = await revealApiKey(api, k.prefix);
    } catch (e) {
      showToast({
        title: 'Reveal failed',
        body: <span style={{ color: 'var(--danger-700)' }}>{e?.message || 'Unknown error.'}</span>,
        variant: 'danger',
        autoHideMs: 6000,
      });
      return;
    }
    const id = showToast({
      title: `Plaintext: ${k.prefix}…`,
      body: (
        <RevealedToastBody
          plaintext={plaintext}
          onHide={() => {
            if (activeIdRef.current) dismissToast(activeIdRef.current);
            setRevealedToastId(null);
          }}
        />
      ),
      variant: 'warning',
      autoHideMs: 30000,
      replace: true,
    });
    setRevealedToastId(id);
  }

  // While the toast is up, swap eye → eye-off and route the click
  // to "hide now" so the operator doesn't have to track the toast.
  const isShowing = revealedToastId !== null;
  if (isShowing) {
    return (
      <IconBtn
        title="Hide revealed plaintext"
        ariaLabel={`Hide revealed plaintext of ${k.prefix}`}
        onClick={() => {
          if (activeIdRef.current) dismissToast(activeIdRef.current);
          setRevealedToastId(null);
        }}
      >
        <EyeOffIcon />
      </IconBtn>
    );
  }
  return (
    <IconBtn
      title="Reveal the plaintext (pk keys only)"
      ariaLabel={`Reveal plaintext of ${k.prefix}`}
      onClick={doReveal}
    >
      <EyeIcon />
    </IconBtn>
  );
}

/**
 * Body for the reveal toast — read-only input + copy + hide.  The
 * input is wider than 280px (the prefix + value together hit ~64
 * chars for a live pk) so we let it scroll horizontally on overflow.
 */
function RevealedToastBody({ plaintext, onHide }) {
  const inputRef = useRef(null);
  const [copied, setCopied] = useState(false);

  function selectAll() {
    const el = inputRef.current;
    if (el) {
      el.focus();
      el.select();
    }
  }

  async function copy() {
    try {
      await navigator.clipboard.writeText(plaintext);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1500);
    } catch {
      // Clipboard rejected (insecure context, perms, etc.) — the
      // input is still visible so the user can ⌘C the selection.
      selectAll();
    }
  }

  return (
    <div className="reveal-toast">
      <p className="reveal-toast__warn">
        Anyone watching your screen right now will see this value.
        It hides automatically in 30 seconds.
      </p>
      <div className="reveal-toast__row">
        <input
          ref={inputRef}
          type="text"
          className="reveal-toast__value"
          readOnly
          value={plaintext}
          onFocus={selectAll}
          onClick={selectAll}
          aria-label="Revealed plaintext"
          spellCheck={false}
        />
        <IconBtn
          title={copied ? 'Copied' : 'Copy to clipboard'}
          ariaLabel="Copy revealed plaintext"
          onClick={copy}
        >
          <CopyToastIcon />
        </IconBtn>
        <IconBtn
          title="Hide now"
          ariaLabel="Hide revealed plaintext now"
          onClick={onHide}
        >
          <EyeOffIcon />
        </IconBtn>
      </div>
    </div>
  );
}

function EditKeyModal({ k, api, onClose, onSaved }) {
  const [name, setName] = useState(k.name || '');
  const [expiresAt, setExpiresAt] = useState(k.expires_at ? k.expires_at.slice(0, 16) : '');
  const [scopeCatalog, setScopeCatalog] = useState(null);
  const [scopeMode, setScopeMode] = useState(k.scopes?.length ? 'scoped' : 'full');
  const [selectedScopes, setSelectedScopes] = useState(new Set(k.scopes || []));
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  useEffect(() => {
    if (scopeMode !== 'scoped' || scopeCatalog) return;
    let alive = true;
    api('/oauth/scopes')
      .then((c) => { if (alive) setScopeCatalog(c); })
      .catch(() => {});
    return () => { alive = false; };
  }, [scopeMode, scopeCatalog, api]);

  function toggleScope(s) {
    const next = new Set(selectedScopes);
    if (next.has(s)) next.delete(s); else next.add(s);
    setSelectedScopes(next);
  }

  async function save(e) {
    e.preventDefault();
    setBusy(true);
    setErr(null);
    try {
      const patch = { name: name.trim() };
      if (scopeMode === 'full') {
        patch.clear_scopes = true;
      } else {
        patch.scopes = Array.from(selectedScopes);
      }
      if (expiresAt) {
        patch.expires_at = new Date(expiresAt).toISOString();
      } else if (k.expires_at) {
        patch.clear_expires_at = true;
      }
      await api(`/v1/admin/api-keys/${encodeURIComponent(k.prefix)}`, {
        method: 'PATCH',
        body: JSON.stringify(patch),
      });
      onSaved();
    } catch (ex) {
      setErr(ex.message);
      setBusy(false);
    }
  }

  return (
    <div className="edit-modal__backdrop" role="dialog" aria-modal="true" onClick={onClose}>
      <form className="edit-modal" onClick={(e) => e.stopPropagation()} onSubmit={save}>
        <div className="edit-modal__head">
          <h3>Edit key</h3>
          <code style={{ color: 'var(--text-3)' }}>{k.prefix}…</code>
          <button type="button" className="edit-modal__close" onClick={onClose} aria-label="Close" title="Close">
            <XIcon size={18} />
          </button>
        </div>
        <div className="edit-modal__body">
          <label className="field">
            <span className="field__label">Name</span>
            <input className="input" value={name} onChange={(e) => setName(e.target.value)} autoFocus />
          </label>
          <label className="field">
            <span className="field__label">Expires (local time — leave blank for no expiry)</span>
            <input
              type="datetime-local"
              className="input"
              value={expiresAt}
              onChange={(e) => setExpiresAt(e.target.value)}
            />
          </label>

          <fieldset style={{ border: '1px solid var(--border-1)', borderRadius: 10, padding: '10px 12px' }}>
            <legend style={{ fontSize: 12, fontWeight: 600, padding: '0 6px', color: 'var(--text-3)' }}>Scopes</legend>
            <div style={{ display: 'flex', gap: 16, marginBottom: 8 }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13 }}>
                <input type="radio" name="edit-scope" checked={scopeMode === 'full'} onChange={() => setScopeMode('full')} />
                Full access
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13 }}>
                <input type="radio" name="edit-scope" checked={scopeMode === 'scoped'} onChange={() => setScopeMode('scoped')} />
                Pick specific scopes
              </label>
            </div>
            {scopeMode === 'scoped' && (
              <ScopePicker
                scopes={scopeCatalog?.scopes || []}
                selected={selectedScopes}
                onToggle={toggleScope}
                loading={!scopeCatalog}
                idPrefix={`edit-${k.prefix}`}
              />
            )}
          </fieldset>

          {err && <div className="state state--error">{err}</div>}
        </div>
        <div className="edit-modal__foot">
          <button type="button" className="btn" onClick={onClose} disabled={busy}>
            <XIcon /> Cancel
          </button>
          <button type="submit" className="btn btn--primary" disabled={busy}>
            <SaveIcon /> {busy ? 'Saving…' : 'Save'}
          </button>
        </div>
        <style>{EDIT_MODAL_CSS}</style>
      </form>
    </div>
  );
}

const EDIT_MODAL_CSS = `
  .edit-modal__backdrop { position: fixed; inset: 0; background: rgba(15,23,42,0.55); display: flex; align-items: center; justify-content: center; z-index: 1000; padding: 16px; }
  .edit-modal { background: var(--surface-0); border-radius: 12px; width: 100%; max-width: 540px; max-height: calc(100vh - 32px); overflow: auto; box-shadow: 0 24px 48px -16px rgba(15,23,42,0.4); display: flex; flex-direction: column; }
  .edit-modal__head { display: flex; align-items: center; gap: 10px; padding: 14px 18px; border-bottom: 1px solid var(--border-1); }
  .edit-modal__head h3 { margin: 0; font-size: 15px; flex: 0 0 auto; }
  .edit-modal__close { margin-left: auto; font: inherit; font-size: 22px; background: transparent; border: 0; cursor: pointer; color: var(--text-3); }
  .edit-modal__body { padding: 16px 18px; display: flex; flex-direction: column; gap: 14px; }
  .edit-modal__foot { display: flex; gap: 8px; justify-content: flex-end; padding: 12px 18px; border-top: 1px solid var(--border-1); background: var(--surface-1); }
`;

export function KindBadge({ kind }) {
  const map = {
    sk: { label: 'secret', cls: 'badge--admin', title: 'sk_ — server-side, full admin access' },
    pk: { label: 'publishable', cls: 'badge--viewer', title: 'pk_ — safe to embed in page source; no admin access' },
    mcp: { label: 'mcp', cls: 'badge--editor', title: 'mcp_ — OAuth-issued; bound to a specific user account' },
  };
  const entry = map[kind] || map.sk;
  return (
    <span className={`badge ${entry.cls}`} style={{ marginLeft: 8 }} title={entry.title}>
      {entry.label}
    </span>
  );
}

export function OwnershipBadge({ k, currentUserId }) {
  const owner = (k.owner_user_id || '').trim();
  // No owner → system-owned.  Survives leavers, no auto-revoke.
  if (!owner) {
    return (
      <span
        className="badge badge--default"
        style={{ marginLeft: 6 }}
        title="System-owned. Survives anyone offboarding. Used by project embeds and shared backend integrations."
      >
        system
      </span>
    );
  }
  // Personal → who owns it.  "yours" when it's the viewer's, plain
  // "personal" with the user id in the tooltip otherwise.
  const mine = currentUserId && owner === currentUserId;
  return (
    <span
      className={`badge ${mine ? 'badge--editor' : 'badge--admin'}`}
      style={{ marginLeft: 6 }}
      title={
        mine
          ? 'Personal key bound to your user. Leaving the tenant auto-revokes it.'
          : `Personal key bound to ${owner}. Offboarding that user auto-revokes it.`
      }
    >
      {mine ? 'yours' : 'personal'}
    </span>
  );
}

/**
 * Card 048 — surface `effective_purpose` inline so an operator can
 * tell at a glance which pk_live_* keys feed the bookmarklet vs.
 * the SDK embed.  Falls back to the raw `purpose` field when the
 * backend response predates the effective_purpose envelope (older
 * deploy + new dashboard) so we never render a blank badge for a
 * key whose purpose we genuinely know.
 */
export function PurposeBadge({ k }) {
  const purpose = (k.effective_purpose || k.purpose || '').trim();
  if (!purpose) return null;
  // Bookmarklet keys get the brand colour — they're the most user-
  // facing surface, the operator clicks them from the bookmark bar
  // daily.  Embed + the rest fall back to the neutral default badge.
  const tone = purpose === 'bookmarklet' ? 'badge--editor' : 'badge--default';
  const title =
    purpose === 'bookmarklet'
      ? 'Used by the dashboard bookmarklet — drag it to your bookmark bar from My API keys.'
      : purpose === 'embed'
      ? 'Embedded in customer-facing pages via the public SDK.'
      : `Tagged purpose=${purpose} for dashboard routing.`;
  return (
    <span className={`badge ${tone}`} style={{ marginLeft: 6 }} title={title}>
      {purpose}
    </span>
  );
}

export function formatDate(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      month: 'short', day: 'numeric', year: 'numeric',
    });
  } catch { return iso; }
}

// Card 044 — row + icon-button + reveal-toast styles.  Keeping them
// in one block so a future visual tweak only touches one place.
const API_KEYS_ROW_CSS = `
  .api-key-row {
    display: flex;
    gap: 12px;
    align-items: flex-start;
  }
  .api-key-row__primary {
    flex: 1;
    min-width: 0;
  }
  .api-key-row__name {
    display: inline-flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 2px 0;
  }
  .api-key-row__meta {
    display: block;
    margin-top: 2px;
  }
  .api-key-row__owner {
    color: var(--text-3, #64748b);
    display: inline-flex;
    align-items: center;
    gap: 4px;
    vertical-align: middle;
  }
  .api-key-row__owner .spot-avatar {
    flex-shrink: 0;
  }
  .api-key-row__actions {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    flex-shrink: 0;
  }

  /* ── Icon button ─────────────────────────────────────────── */
  .icon-btn {
    width: 28px;
    height: 28px;
    padding: 0;
    background: transparent;
    border: 1px solid transparent;
    border-radius: 6px;
    color: var(--text-2, #334155);
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    transition: background 0.12s, color 0.12s, border-color 0.12s;
  }
  .icon-btn:hover {
    background: var(--surface-1, #f1f5f9);
    color: var(--text-1, #0f172a);
  }
  .icon-btn:focus-visible {
    outline: 2px solid var(--brand-500, #5b6cff);
    outline-offset: 1px;
  }
  .icon-btn--danger:hover {
    background: var(--danger-50, #fef2f2);
    color: var(--danger-700, #b91c1c);
  }
  .icon-btn--disabled,
  .icon-btn[aria-disabled="true"] {
    opacity: 0.4;
    cursor: not-allowed;
  }
  .icon-btn--disabled:hover,
  .icon-btn[aria-disabled="true"]:hover {
    background: transparent;
    color: var(--text-2, #334155);
  }

  /* ── Reveal toast body ───────────────────────────────────── */
  .reveal-toast { display: flex; flex-direction: column; gap: 8px; }
  .reveal-toast__warn {
    margin: 0;
    font-size: 12px;
    color: var(--warn-700, #b45309);
    line-height: 1.4;
  }
  .reveal-toast__row {
    display: flex;
    align-items: center;
    gap: 4px;
  }
  .reveal-toast__value {
    flex: 1;
    min-width: 0;
    font-family: ui-monospace, SFMono-Regular, "SF Mono", Menlo, Consolas, monospace;
    font-size: 12.5px;
    padding: 6px 8px;
    background: var(--surface-2, #f1f5f9);
    border: 1px solid var(--border-1, #e2e8f0);
    border-radius: 6px;
    color: var(--text-1, #0f172a);
    /* Single-line + horizontal-scroll-on-overflow.  Prevents the
       long pk_live_* string wrapping across two lines like it used
       to inside the old <code> block. */
    white-space: nowrap;
    overflow-x: auto;
  }
  .reveal-toast__value:focus {
    outline: 2px solid var(--brand-500, #5b6cff);
    outline-offset: 0;
    border-color: transparent;
  }
`;
